import { useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  ClipboardCheck, X, Loader2, AlertCircle, ArrowRight, Plus, Trash2, ChevronRight, Search, Download,
} from "lucide-react";
import SignaturePad from "@/components/SignaturePad";
import { matchesQuery } from "@/components/LiveSearch";
import { todayLocalYmd } from "@/lib/workOrders";

const PJC_QUESTIONS = [
  { section: "Seguridad general", id: "seg", items: [
    ["riesgos_identificados", "¿Se han identificado los riesgos del trabajo?"],
    ["zona_segura", "¿La zona de trabajo está segura y despejada?"],
    ["herramientas_ok", "¿Las herramientas están en buen estado?"],
  ]},
  { section: "EPIs", id: "epi", items: [
    ["epi_completos", "¿Llevas los EPIs completos (casco, guantes, calzado)?"],
    ["epi_estado", "¿Los EPIs están en buen estado?"],
  ]},
  { section: "Formación", id: "form", items: [
    ["formacion_ok", "¿Tienes la formación necesaria para esta tarea?"],
  ]},
  { section: "Riesgos previstos durante el trabajo", id: "risk", mode: "info",
    hint: "Marca lo que corresponda. Puedes firmar aunque marques NO.",
    items: [
    ["altura", "Trabajos en altura → ¿arnés / línea de vida?"],
    ["electrico", "Riesgo eléctrico → ¿bloqueo y consignación?"],
    ["quimico", "Riesgo químico → ¿medidas de contención?"],
  ]},
  {
    section: "Plan de ejecución",
    id: "plan",
    mode: "any",
    hint: "Marca SÍ en la opción que corresponda. Basta con una.",
    items: [
      ["plan_especifico", "De acuerdo con el Plan Específico o Plan de Seguridad"],
      ["plan_prevencion", "De acuerdo con el Plan Específico de Prevención"],
      ["plan_general", "De acuerdo con el Plan General de Prevención de la empresa"],
    ],
  },
];

function evaluatePjcSection(sec, answers) {
  const vals = sec.items.map(([k]) => answers[k]);
  const hasSi = vals.some((v) => v === "si");
  const hasNo = vals.some((v) => v === "no");
  const allAnswered = vals.every((v) => v === "si" || v === "no");
  if (sec.mode === "any") return { ok: hasSi, hasBlockingNo: !hasSi && hasNo };
  // Informational section: any combination (SI/NO) is valid as long as every
  // question is answered. NO does NOT block signing.
  if (sec.mode === "info") return { ok: allAnswered, hasBlockingNo: false };
  return { ok: allAnswered && !hasNo, hasBlockingNo: hasNo };
}

// Flatten answers → readable label list for detail view
function _labelFor(key) {
  for (const s of PJC_QUESTIONS) for (const [k, l] of s.items) if (k === key) return l;
  return key;
}

// ─────────────────────────────────────────────────────────────────────────
export default function MyPjc() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pjc/mine");
      setItems(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cargar tu historial"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="my-pjc-page">
      <header>
        <div className="label-up text-[#0E78C2]">Seguridad</div>
        <h1 className="font-display font-black text-3xl tracking-tight mt-1">Mi Pre-Job Check</h1>
        <p className="text-zinc-600 text-sm mt-1">Rellena antes de empezar un trabajo. Guardamos tu historial completo.</p>
      </header>

      <button type="button" onClick={() => setFormOpen(true)} data-testid="my-pjc-fill-btn"
        className="brutal-card w-full p-4 flex items-center gap-3 bg-[#F5A623] text-white border-[#8B4513] hover:-translate-y-[1px] transition-transform">
        <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
          <ClipboardCheck size={20} />
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="label-up text-white/80">Nuevo</div>
          <div className="font-display font-black text-lg leading-tight">Rellenar Pre-Job Check</div>
        </div>
        <ArrowRight size={16} className="shrink-0" />
      </button>

      <div>
        <h2 className="label-up mb-2">Historial ({items.length})</h2>
        {loading && items.length === 0 && (
          <div className="brutal-card p-6 text-center text-slate-500 inline-flex items-center gap-2 justify-center w-full">
            <Loader2 size={16} className="animate-spin" /> Cargando…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="brutal-card p-6 text-center text-slate-500">Aún no has rellenado ningún PJC.</div>
        )}
        <ul className="space-y-2">
          {items.map((p) => (
            <li key={p.id}>
              <button type="button" onClick={() => setDetail(p)}
                data-testid={`my-pjc-item-${p.id}`}
                className="w-full brutal-card p-3 flex items-center gap-3 text-left hover:-translate-y-[1px] transition-transform">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                  <ClipboardCheck size={18} className="text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{p.obra || "Sin obra"}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {p.fecha} · {(p.firmantes_extra?.length || 0) + 1} firma{p.firmantes_extra?.length ? "s" : ""}
                  </div>
                </div>
                <ChevronRight size={16} className="text-slate-400 shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      </div>

      {formOpen && (
        <FillPjcModal
          onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); load(); }}
          defaultObra={items[0]?.obra || ""}
        />
      )}
      {detail && <DetailPjcModal pjc={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

// Download a PJC as PDF (auth-aware) using the /api/pjc/{id}/pdf endpoint.
async function downloadPjcPdf(pjc) {
  try {
    const res = await fetch(`${API}/pjc/${pjc.id}/pdf`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("etel_token")}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safe = (pjc.obra || "PJC").replace(/[^\w]+/g, "_").slice(0, 40);
    a.href = url;
    a.download = `PJC_${safe}_${pjc.fecha || ""}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch (e) {
    toast.error("No se pudo descargar el PDF");
  }
}

// ─────────────────────────────────────────────────────────────────────────
function DetailPjcModal({ pjc, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose} data-testid="my-pjc-detail-modal">
      <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div>
            <div className="label-up text-[#0E78C2]">PJC</div>
            <h3 className="font-display font-black text-lg text-[#0B2A4A] leading-tight">{pjc.obra}</h3>
            <div className="text-[11px] text-slate-500">{pjc.fecha}</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-rose-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <button type="button" onClick={() => downloadPjcPdf(pjc)}
            data-testid="my-pjc-download-pdf"
            className="w-full h-10 rounded-lg bg-[#0E78C2] text-white text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#0a5b93]">
            <Download size={14} /> Descargar PDF
          </button>
          <div>
            <div className="label-up mb-1">Respuestas</div>
            <ul className="text-xs space-y-1">
              {Object.entries(pjc.answers || {}).map(([k, v]) => (
                <li key={k} className="flex items-center gap-2">
                  <span className={`inline-flex items-center justify-center w-6 h-5 rounded text-[10px] font-bold ${
                    v === "si" ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"
                  }`}>{v === "si" ? "SÍ" : "NO"}</span>
                  <span className="text-slate-700 truncate">{_labelFor(k)}</span>
                </li>
              ))}
            </ul>
          </div>
          {pjc.notas && (
            <div>
              <div className="label-up mb-1">Notas</div>
              <p className="text-sm whitespace-pre-wrap text-slate-700">{pjc.notas}</p>
            </div>
          )}
          <div>
            <div className="label-up mb-2">Firmas ({(pjc.firmantes_extra?.length || 0) + 1})</div>
            <div className="space-y-2">
              <SignatureRow name={pjc.user_name} signature={pjc.signature} />
              {(pjc.firmantes_extra || []).map((f, i) => (
                <SignatureRow key={i} name={f.name} signature={f.signature} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SignatureRow({ name, signature }) {
  return (
    <div className="flex items-center gap-3 border border-slate-200 rounded-lg p-2">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[#0B2A4A] truncate">{name || "—"}</div>
      </div>
      {signature ? (
        <img src={signature} alt="firma" className="h-12 w-24 object-contain bg-slate-50 border border-slate-200 rounded" />
      ) : (
        <div className="h-12 w-24 bg-slate-50 border border-dashed border-slate-300 rounded inline-flex items-center justify-center text-[10px] text-slate-400">
          sin firma
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
function FillPjcModal({ onClose, onSaved, defaultObra = "" }) {
  const [answers, setAnswers] = useState({});
  const [obra, setObra] = useState(defaultObra);
  const [notas, setNotas] = useState("");
  const [extras, setExtras] = useState([]); // {name, user_id, sigRef}
  const [saving, setSaving] = useState(false);
  const [directory, setDirectory] = useState([]);
  const selfSigRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get("/users/directory")
      .then(({ data }) => { if (alive) setDirectory(data || []); })
      .catch(() => { /* silent — dropdown will simply be empty */ });
    return () => { alive = false; };
  }, []);

  const sectionResults = PJC_QUESTIONS.map((sec) => ({ sec, ...evaluatePjcSection(sec, answers) }));
  const answersOk = sectionResults.every((r) => r.ok);
  const anyBlockingNo = sectionResults.some((r) => r.hasBlockingNo);
  const setAnswer = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const addExtra = () => setExtras((e) => [...e, { name: "", user_id: null, ref: { current: null } }]);
  const rmExtra = (i) => setExtras((e) => e.filter((_, idx) => idx !== i));
  const setExtraName = (i, n, user_id = null) =>
    setExtras((e) => e.map((x, idx) => idx === i ? { ...x, name: n, user_id } : x));

  const submit = async () => {
    if (!obra.trim()) { toast.error("El nombre de la obra es obligatorio"); return; }
    if (!answersOk) { toast.error(anyBlockingNo ? "Hay respuestas NO bloqueantes" : "Faltan respuestas"); return; }
    const selfSig = selfSigRef.current?.getDataURL();
    if (!selfSig || selfSigRef.current?.isEmpty) { toast.error("Firma tu PJC"); return; }
    const extrasData = [];
    for (let i = 0; i < extras.length; i++) {
      const e = extras[i];
      if (!e.name.trim()) { toast.error(`Nombre del firmante #${i + 1} vacío`); return; }
      const sig = e.ref.current?.getDataURL();
      if (!sig || e.ref.current?.isEmpty) { toast.error(`Falta la firma de ${e.name}`); return; }
      extrasData.push({ name: e.name.trim(), signature: sig, user_id: e.user_id || null });
    }
    setSaving(true);
    try {
      await api.post("/pjc/mine", {
        fecha: todayLocalYmd(),
        obra: obra.trim(),
        answers,
        signature: selfSig,
        firmantes_extra: extrasData,
        notas,
      });
      toast.success("Pre-Job Check guardado");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose} data-testid="my-pjc-form-modal">
      <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <h3 className="font-display font-black text-lg text-[#0B2A4A]">Rellenar Pre-Job Check</h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-rose-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="label-up block mb-1">Nombre de obra *</label>
            <input className="brutal-input w-full" required data-testid="my-pjc-obra"
              value={obra} onChange={(e) => setObra(e.target.value)}
              placeholder="Nombre" />
          </div>

          {sectionResults.map(({ sec, ok: sectionOk }) => (
            <fieldset key={sec.id} data-testid={`my-pjc-section-${sec.id}`}>
              <legend className="label-up mb-2">{sec.section}</legend>
              {sec.hint && <p className="text-[11px] text-slate-500 mb-2 -mt-1">{sec.hint}</p>}
              <div className="space-y-2">
                {sec.items.map(([key, label]) => {
                  const v = answers[key];
                  const noBlocking = v === "no" && !(sec.mode === "any" && sectionOk);
                  return (
                    <div key={key} className={`rounded-lg border p-3 ${
                      noBlocking ? "bg-rose-50 border-rose-300" : "bg-white border-slate-200"
                    }`}>
                      <p className="text-xs text-slate-700 mb-2">{label}</p>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setAnswer(key, "si")} data-testid={`my-pjc-${key}-si`}
                          className={`flex-1 h-10 rounded-lg text-xs font-bold ${
                            v === "si" ? "bg-emerald-600 text-white shadow-md" : "bg-slate-100 text-slate-500 hover:bg-emerald-100"
                          }`}>SÍ</button>
                        <button type="button" onClick={() => setAnswer(key, "no")} data-testid={`my-pjc-${key}-no`}
                          className={`flex-1 h-10 rounded-lg text-xs font-bold ${
                            v === "no" ? "bg-rose-600 text-white shadow-md" : "bg-slate-100 text-slate-500 hover:bg-rose-100"
                          }`}>NO</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </fieldset>
          ))}

          <div>
            <label className="label-up block mb-1">Notas (opcional)</label>
            <textarea className="brutal-input w-full min-h-[60px]" data-testid="my-pjc-notas"
              value={notas} onChange={(e) => setNotas(e.target.value)} />
          </div>

          {anyBlockingNo && (
            <div className="rounded-xl bg-rose-600 text-white p-4 border-2 border-rose-800" data-testid="my-pjc-danger">
              <div className="font-black text-sm uppercase tracking-wide flex items-start gap-2">
                <AlertCircle size={20} className="shrink-0 mt-0.5" />
                <span>DEBES DETENER LAS LABORES Y PONERTE EN CONTACTO CON UN SUPERIOR</span>
              </div>
            </div>
          )}

          <div>
            <div className="label-up mb-1">Tu firma *</div>
            <SignaturePad ref={selfSigRef} height={120} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="label-up">Otros firmantes ({extras.length})</span>
              <button type="button" onClick={addExtra} data-testid="my-pjc-add-extra"
                className="text-[11px] font-bold uppercase tracking-wider text-[#0E78C2] hover:underline inline-flex items-center gap-1">
                <Plus size={12} /> Añadir compañero
              </button>
            </div>
            {extras.length === 0 && (
              <p className="text-[11px] text-slate-500">Añade compañeros que también deben firmar este PJC.</p>
            )}
            <div className="space-y-3">
              {extras.map((ex, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`my-pjc-extra-${i}`}>
                  <div className="flex items-start gap-2">
                    <UserAutocomplete
                      value={ex.name}
                      onChange={(n, uid) => setExtraName(i, n, uid)}
                      directory={directory}
                      excludeNames={extras.filter((_, idx) => idx !== i).map((x) => x.name)}
                      testId={`my-pjc-extra-name-${i}`}
                      className="flex-1"
                    />
                    <button type="button" onClick={() => rmExtra(i)}
                      data-testid={`my-pjc-extra-rm-${i}`}
                      className="w-9 h-9 rounded-md border border-rose-300 text-rose-600 hover:bg-rose-50 inline-flex items-center justify-center shrink-0">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <SignaturePad ref={ex.ref} height={100} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex gap-2">
          <button type="button" onClick={onClose}
            className="flex-1 h-11 rounded-lg border border-slate-200 text-slate-600 text-sm font-semibold">Cancelar</button>
          <button type="button" onClick={submit} disabled={saving}
            data-testid="my-pjc-submit"
            className="flex-1 h-11 rounded-lg bg-[#FF4500] text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            Firmar y guardar
          </button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Searchable dropdown that pulls names from the app's user directory.
// Filters live as the user types (case- and diacritic-insensitive).
// The user may also free-type a name that is not in the list (fallback).
function UserAutocomplete({ value, onChange, directory, excludeNames = [], testId, className = "" }) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const excludeSet = useMemo(
    () => new Set(excludeNames.map((n) => (n || "").trim().toLowerCase()).filter(Boolean)),
    [excludeNames],
  );

  const matches = useMemo(() => {
    const q = value || "";
    return (directory || [])
      .filter((u) => !excludeSet.has((u.name || "").trim().toLowerCase()))
      .filter((u) => matchesQuery(q, u.name, u.email, u.empresa, u.departamento))
      .slice(0, 8);
  }, [value, directory, excludeSet]);

  const showList = open && focused && matches.length > 0;

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          className="brutal-input w-full pl-9"
          placeholder="Buscar compañero…"
          data-testid={testId}
          value={value || ""}
          onChange={(e) => { onChange(e.target.value, null); setOpen(true); }}
          onFocus={() => { setFocused(true); setOpen(true); }}
          onBlur={() => setFocused(false)}
          autoComplete="off"
        />
      </div>
      {showList && (
        <ul
          className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg"
          data-testid={`${testId}-dropdown`}
        >
          {matches.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onChange(u.name, u.id); setOpen(false); }}
                data-testid={`${testId}-opt-${u.id}`}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
              >
                <div className="text-sm font-semibold text-[#0B2A4A] truncate">{u.name}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {[u.empresa, u.departamento].filter(Boolean).join(" · ") || u.email}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
